import { COLORS, SPACING } from '@alto/design-library-tokens';
import { ActionSheetContext, AltoSpinningLoader, Column, LgPadding, XxsSpacing } from '@alto/design-system';
import {
  AnnouncementNotifier,
  CompletedTransferSection,
  NewYearInsuranceCard,
  PendingTransferSection,
  PromosSection,
  UnboxingGuideCard,
  useFetchHomescreen,
  useQueryInstructionalVideos,
} from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { type DeliveredShipment } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/delivered_shipment';
import { type Patient } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/patient';
import { type PatientTask } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/patient_task';
import { type Prescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/prescription';
import { type RenewalPrescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/renewal_prescription';
import { type Section } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/section';
import { SectionTypeMap } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/section_type';
import { type SourcePrescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/source_prescription';
import { type UpcomingShipment as UpcomingShipmentType } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/upcoming_shipment';
import { PatientTaskTypeMap } from '@alto/scriptdash/alto/patient_app/patient_tasks/types/v1/patient_task_type';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { TypescriptUtils } from '~shared/TypescriptUtils';
import { fillHomescreenBanner } from '~shared/actions/homescreenFillBanner';
import { setBadgeCounts } from '~shared/actions/notices';
import { ORGANIC_ONBOARDING_STEPS } from '~shared/constants';
import { generateSectionCounts } from '~shared/features/homescreen/analytics';
import { getIsOnboardingComplete } from '~shared/features/onboarding/selectors/getIsOnboardingComplete';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getEligibleViewedPromos } from '~shared/selectors/ui/promos';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { ContentWrapper, Main, PageWithHeader } from './PageWithHeader';
import { DeliveredShipmentSection } from '~web/features/homescreen/sections/DeliveredShipment';
import DueForRefillPrescriptions from '~web/features/homescreen/sections/DueForRefillPrescriptions';
import { InjectionGuides } from '~web/features/homescreen/sections/InjectionGuides';
import { NeedsInsuranceCard } from '~web/features/homescreen/sections/NeedsInsuranceCard';
import NewPrescriptions from '~web/features/homescreen/sections/NewPrescriptions';
import NoNewUpdates from '~web/features/homescreen/sections/NoNewUpdates';
import { OutOfRefillsCard } from '~web/features/homescreen/sections/OutOfRefillsCard';
import { SendMedications } from '~web/features/homescreen/sections/SendMedications';
import UnconfirmedShipment from '~web/features/homescreen/sections/UnconfirmedShipment';
import UpcomingShipment from '~web/features/homescreen/sections/UpcomingShipment';
import UpdatePaymentMethod from '~web/features/homescreen/sections/UpdatePaymentMethod';
import { OnboardingResumeActionSheet } from '~web/features/onboarding/components/OnboardingResumeActionSheet';
import {
  OnboardingMedicalInfoActionSheet,
  getOrganicOnboardingStep,
} from '~web/features/onboarding/components/OrganicOnboarding';

const LoaderContainer = styled.div`
  z-index: 1;
  margin-top: -7rem;
`;

const HOMESCREEN_CONTENT_MAX_WIDTH = '800px';

const HomescreenMain = styled(Main)`
  max-width: ${HOMESCREEN_CONTENT_MAX_WIDTH};
  margin-left: auto;
  margin-right: auto;
`;

export const Homescreen = () => {
  const { navigate } = useNavigation();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const currentUser = useSelectorShared(getCurrentUser);
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatchShared();
  const { isLoading, data: response, refetch } = useFetchHomescreen(currentUser?.id);
  const onboarding = useSelectorShared((state) => state.auth.onboarding);
  const promoState = useSelectorShared((state) => state.persistedUI.promos);
  const { videos } = useQueryInstructionalVideos();

  useEffect(() => {
    if (!isLoading) {
      trackEvent({ event: EVENTS.HOMESCREEN_VIEWED, params: generateSectionCounts(response?.data.sections) });
    }
  }, [isLoading, trackEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const patientTaskSection = response?.data.sections.find((section) => section.type === SectionTypeMap.PATIENT_TASK);
  const patientTasks = patientTaskSection?.patient_tasks?.length ?? 0;
  useEffect(() => {
    dispatch(setBadgeCounts({ patientTasks }));
  }, [dispatch, patientTasks]);

  const hasSeenOnboarding = useRef(false);
  // open organic onboarding modal
  const currentStep = currentUser ? getOrganicOnboardingStep(currentUser) : ORGANIC_ONBOARDING_STEPS.HEALTH_PROFILE;
  useEffect(() => {
    if (!hasSeenOnboarding.current && onboarding) {
      setActiveActionSheet(<OnboardingMedicalInfoActionSheet />, { dismissible: false });
      hasSeenOnboarding.current = true;
    }
  }, [currentStep, dispatch, onboarding, setActiveActionSheet]);

  // resume organic onboarding modal
  const isOnboardingComplete = useSelectorShared(getIsOnboardingComplete);
  useEffect(() => {
    if (!hasSeenOnboarding.current && !onboarding && !isOnboardingComplete) {
      setActiveActionSheet(<OnboardingResumeActionSheet />, { dismissible: false });
      hasSeenOnboarding.current = true;
    }
  }, [isOnboardingComplete, onboarding, setActiveActionSheet]);

  const onViewDelivery = useCallback(
    (shipmentID: number) => {
      navigate('RouteOrderDetails', { shipmentID });
    },
    [navigate],
  );

  if (isLoading || !response) {
    return (
      <PageWithHeader
        title=""
        bgColor={COLORS.BACKGROUND_COLORS.WHITE}
        hasSections
      >
        <LoaderContainer aria-label="loading">
          <Column center>
            <AltoSpinningLoader />
          </Column>
        </LoaderContainer>
      </PageWithHeader>
    );
  }

  const {
    patient,
    sections,
  }: {
    patient: Patient;
    sections: Section[];
  } = response.data;

  const renderPatientTasks = (tasks: PatientTask[]) => {
    return tasks.map((task) => {
      switch (task.type) {
        case PatientTaskTypeMap.CONFIRM_SHIPMENT: {
          return task.unconfirmed_shipment ? (
            <UnconfirmedShipment
              key={task.unconfirmed_shipment.id}
              shipment={task.unconfirmed_shipment}
              refetchHomescreen={refetch}
              onViewDelivery={onViewDelivery}
            />
          ) : null;
        }

        case PatientTaskTypeMap.NEEDS_INSURANCE: {
          // @ts-expect-error: Properties do not exist on type 'NeedsInsurancePatientTask | null | undefined'.
          const { user_id, family_member_name } = task.needs_insurance;
          return (
            <NeedsInsuranceCard
              key={`needs-insurance-${user_id}-${family_member_name}`}
              userID={user_id}
              name={family_member_name}
            />
          );
        }

        case PatientTaskTypeMap.NEW_YEAR_INSURANCE: {
          // @ts-expect-error: Properties do not exist on type 'NewYearInsurancePatientTask | null | undefined'.
          const { user_id } = task.new_year_insurance;
          return (
            <NewYearInsuranceCard
              key={`new-year-insurance-${user_id}`}
              userID={user_id}
              taskID={Number(task.id)}
              onInsuranceDismissal={refetch}
            />
          );
        }

        case PatientTaskTypeMap.UPDATE_PAYMENT_METHOD: {
          const updatePayment = task.update_payment_method;
          const paymentMethods = updatePayment?.shipment ? updatePayment.payment_methods : [];
          return updatePayment?.shipment && paymentMethods.length > 0 ? (
            <UpdatePaymentMethod
              key={updatePayment.shipment.id}
              shipment={updatePayment.shipment}
              paymentMethods={paymentMethods}
              onViewDelivery={onViewDelivery}
            />
          ) : null;
        }

        default:
          return null;
      }
    });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const renderSectionContent = (section: Section, isTopSection: boolean) => {
    switch (section.type) {
      case SectionTypeMap.PATIENT_TASK: {
        return section.patient_tasks ? (
          <React.Fragment>{renderPatientTasks(section.patient_tasks).filter(TypescriptUtils.isPresent)}</React.Fragment>
        ) : null;
      }

      case SectionTypeMap.UNBOXING_GUIDE: {
        return <UnboxingGuideCard />;
      }

      case SectionTypeMap.DELIVERED_SHIPMENTS: {
        // When the section type is DELIVERED_SHIPMENTS, we know prescriptions will be of type Array<DeliveredShipment>
        // Manually refine the type to be of type Array<DeliveredShipment> instead of UnionTypeShipment[]
        const { shipments } = section;
        return (
          shipments && (
            <DeliveredShipmentSection
              delivery={shipments[0] as DeliveredShipment}
              onViewDelivery={onViewDelivery}
            />
          )
        );
      }

      case SectionTypeMap.UPCOMING_SHIPMENTS: {
        // When the section type is UPCOMING_SHIPMENTS, we know prescriptions will be of type Array<UpcomingShipment>
        // Manually refine the type to be of type Array<UpcomingShipment> instead of UnionTypeShipment[]
        const { shipments } = section;
        return shipments ? <UpcomingShipment upcomingShipment={shipments[0] as UpcomingShipmentType} /> : null;
      }

      case SectionTypeMap.PENDING_TRANSFERS: {
        if (!section.transfers || section.transfers.length === 0) {
          return null;
        }

        return (
          <PendingTransferSection
            transfers={section.transfers}
            refetchHomescreen={refetch}
          />
        );
      }

      case SectionTypeMap.CLOSED_TRANSFERS:
        if (!section.transfers || section.transfers.length === 0) {
          return null;
        }

        return (
          <CompletedTransferSection
            transfers={section.transfers}
            onPress={() => {
              navigate('RouteNotificationCenter');
            }}
          />
        );

      case SectionTypeMap.NEW_PRESCRIPTIONS: {
        // When the section type is NEW_PRESCRIPTIONS, we know prescriptions will be of type Array<SourcePrescription | Prescription>
        // Manually refine the type to be of type Array<SourcePrescription | Prescription> instead of UnionTypePrescription[]
        const { prescriptions } = section;
        return section.prescriptions ? (
          <NewPrescriptions prescriptions={prescriptions as (SourcePrescription | Prescription)[]} />
        ) : null;
      }

      case SectionTypeMap.INSTRUCTIONAL_VIDEOS: {
        return <InjectionGuides />;
      }

      case SectionTypeMap.DUE_FOR_REFILL: {
        // When the section type is DUE_FOR_REFILL, we know prescriptions will be of type Array<RenewalPrescription | Prescription>
        // Manually refine the type to be of type Array<RenewalPrescription | Prescription> instead of UnionTypePrescription[]
        const { prescriptions } = section;
        return (
          <DueForRefillPrescriptions
            prescriptions={prescriptions as Prescription[]}
            isTopSection={isTopSection}
          />
        );
      }

      case SectionTypeMap.OUT_OF_REFILLS: {
        // When the section type is OUT_OF_REFILLS, we know prescriptions will be of type Array<RenewalPrescription | Prescription>
        // Manually refine the type to be of type Array<RenewalPrescription | Prescription> instead of UnionTypePrescription[]
        const { prescriptions } = section;
        return <OutOfRefillsCard renewalPrescriptions={prescriptions as RenewalPrescription[]} />;
      }

      case SectionTypeMap.APP_PROMOS: {
        const { promos } = section;
        if (!promos) {
          return null;
        }

        const eligiblePromos = getEligibleViewedPromos(promoState, promos);
        if (eligiblePromos.length === 0) {
          return null;
        }

        return <PromosSection promos={eligiblePromos} />;
      }

      default:
        return null;
    }
  };
  const renderSections = (sections: Section[]) => {
    return sections.map((section, i) => {
      const isTopSection = i === 0;
      const renderTopSpacing = () => (!isTopSection ? <XxsSpacing /> : null);
      return (
        <React.Fragment key={`${section.type}-section`}>
          {renderTopSpacing()}
          {renderSectionContent(section, isTopSection)}
        </React.Fragment>
      );
    });
  };

  const homescreenBanner = fillHomescreenBanner(sections, patient.name);
  const hasSections =
    sections.length > 0 &&
    (sections.length > 1 || sections[0]?.type !== SectionTypeMap.INSTRUCTIONAL_VIDEOS || (videos && videos.length > 0));

  return (
    <PageWithHeader
      title={hasSections ? homescreenBanner.copy : ''}
      bgColor={hasSections ? COLORS.BACKGROUND_COLORS.WHITE : COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      headerBgColor={COLORS.BACKGROUND_COLORS[homescreenBanner.backgroundColor]}
      hasSections={!!hasSections}
      titleMaxWidth={HOMESCREEN_CONTENT_MAX_WIDTH}
    >
      <ContentWrapper>
        <HomescreenMain>
          <LgPadding
            topPadding={SPACING.STATIC.NONE}
            bottomPadding={SPACING.STATIC.NONE}
          >
            <Column
              gap="LG"
              wrap={false}
            >
              {hasSections ? renderSections(sections) : <NoNewUpdates title={homescreenBanner.copy} />}
              <SendMedications />
            </Column>
          </LgPadding>
        </HomescreenMain>
      </ContentWrapper>
      <AnnouncementNotifier />
    </PageWithHeader>
  );
};
